/**
 * ParentLinkingService - Centralized validation for parent-child relationships
 *
 * Prevents duplicate links by checking existing active links before creation.
 * Single source of truth used by all linking entry points.
 */

import { collection, doc, getDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';

const callValidateAccessCode = httpsCallable(functions, 'validateParentAccessCode');
const callRedeemAccessCode = httpsCallable(functions, 'redeemParentAccessCode');
const callCompleteParentSignup = httpsCallable(functions, 'completeParentSignup');

const ACCESS_CODE_ERRORS = {
  code_not_found: 'Invalid code. Please check and try again.',
  code_expired: 'This code has expired. Please contact the school for a new code.',
  code_inactive: 'This code has been deactivated. Please contact the school.',
  student_not_found: 'Student not found. Contact the school.',
};

function withValidationError(result) {
  return {
    ...result,
    error: result.status === 'success' ? null : ACCESS_CODE_ERRORS[result.status] || 'The code could not be checked.',
  };
}

/**
 * Check if an active link already exists between a parent and student
 *
 * @param {string} parentId - The parent's user ID
 * @param {string} studentId - The student's ID
 * @returns {Promise<{exists: boolean, link: object|null, studentName: string|null}>}
 */
export async function checkExistingLink(parentId, studentId) {
  if (!parentId || !studentId) {
    return { exists: false, link: null, studentName: null };
  }

  const linkId = `${parentId}_${studentId}`;
  const linkRef = doc(db, 'parentChildLinks', linkId);

  let linkDoc;
  try {
    linkDoc = await getDoc(linkRef);
  } catch (err) {
    // Fresh parents without linkedStudentIds can't read parentChildLinks
    // Treat permission-denied as "no link exists" (safe assumption)
    if (err.code === 'permission-denied') {
      console.log('[parentLinkingService] Permission denied reading link - treating as no existing link');
      return { exists: false, link: null, studentName: null };
    }
    throw err; // Re-throw other errors
  }

  if (linkDoc.exists()) {
    const linkData = linkDoc.data();

    // Only consider active links as duplicates
    if (linkData.status === 'active') {
      // Fetch student name for display
      let studentName = null;
      if (linkData.schoolId && linkData.studentId) {
        try {
          const studentRef = doc(db, 'schools', linkData.schoolId, 'students', linkData.studentId);
          const studentDoc = await getDoc(studentRef);
          if (studentDoc.exists()) {
            const student = studentDoc.data();
            studentName = `${student.firstName} ${student.surname || student.lastName || ''}`.trim();
          }
        } catch (err) {
          console.error('[parentLinkingService] Error fetching student:', err);
        }
      }

      return {
        exists: true,
        link: { id: linkDoc.id, ...linkData },
        studentName
      };
    }
  }

  return { exists: false, link: null, studentName: null };
}

/**
 * Check for existing link by access code (before full linking flow)
 * Returns existing link info if the code's student is already linked to parent
 *
 * @param {string} parentId - The parent's user ID
 * @param {string} accessCode - The access code entered by parent
 * @returns {Promise<{exists: boolean, link: object|null, studentName: string|null, codeData: object|null, expired: boolean}>}
 */
export async function checkExistingLinkByCode(parentId, accessCode) {
  if (!parentId || !accessCode) {
    return { exists: false, link: null, studentName: null, codeData: null, expired: false };
  }

  const validation = await validateAccessCodeWithDiagnostics(accessCode);
  if (validation.status !== 'success') {
    return {
      exists: false,
      link: null,
      studentName: null,
      codeData: null,
      expired: validation.status === 'code_expired',
    };
  }
  const { codeData } = validation;

  // Check if link already exists for this student
  const existingCheck = await checkExistingLink(parentId, codeData.studentId);

  return {
    ...existingCheck,
    codeData,
    expired: false
  };
}

/**
 * Validate access code and return student info
 * Use this when you need code validation but will handle link creation separately
 *
 * @param {string} accessCode - The access code entered by parent
 * @returns {Promise<{valid: boolean, codeData: object|null, studentData: object|null, error: string|null}>}
 */
export async function validateAccessCode(accessCode) {
  if (!accessCode) {
    return { valid: false, codeData: null, studentData: null, error: 'Please enter an access code' };
  }

  const result = await validateAccessCodeWithDiagnostics(accessCode);
  return {
    valid: result.status === 'success',
    codeData: result.codeData,
    studentData: result.studentData,
    error: result.error,
  };
}

/**
 * Validate an access code with detailed diagnostics for failed attempts.
 * When the compound query (code + isActive) returns empty, does a second
 * query for just the code to distinguish "doesn't exist" vs "inactive/expired".
 *
 * @param {string} accessCode - The access code entered by parent
 * @returns {Promise<{status: string, codeData: object|null, studentData: object|null, error: string|null}>}
 *   status: 'success' | 'code_not_found' | 'code_expired' | 'code_inactive' | 'student_not_found'
 */
export async function validateAccessCodeWithDiagnostics(accessCode) {
  if (!accessCode) {
    return { status: 'code_not_found', codeData: null, studentData: null, error: 'Please enter an access code' };
  }

  try {
    const response = await callValidateAccessCode({ code: accessCode });
    return withValidationError(response.data);
  } catch (error) {
    if (error.code === 'functions/invalid-argument') {
      return withValidationError({ status: 'code_not_found', codeData: null, studentData: null });
    }
    throw error;
  }
}

export async function redeemAccessCode(accessCode, relationship) {
  const response = await callRedeemAccessCode({
    code: accessCode,
    ...(relationship ? { relationship } : {}),
  });
  return response.data;
}

/**
 * Establish the Parent role and school membership for a newly authenticated
 * account. The callable revalidates the bearer code and derives every
 * authority-bearing field server-side; this client passes no role or school.
 * Child linking remains a separate redemption step.
 *
 * @param {string} accessCode - School-issued Parent access code
 * @returns {Promise<{schoolId: string, parent: true, setupComplete: true, alreadyConfigured: boolean}>}
 */
export async function completeParentSignup(accessCode) {
  const response = await callCompleteParentSignup({ code: accessCode });
  return response.data;
}

/**
 * Log a linking attempt to schools/{schoolId}/linkingAttempts for admin visibility.
 *
 * @param {object} details
 * @param {string} details.schoolId - School ID (from code data or user's assignedSchoolId)
 * @param {string} details.parentId - Parent's UID
 * @param {string} details.parentEmail - Parent's email
 * @param {string} details.parentName - Parent's display name
 * @param {string} details.codeEntered - The raw code they typed
 * @param {string} details.status - Stable outcome category
 * @param {string} [details.errorDetail] - Human-readable detail
 * @param {object} [details.codeData] - If code was found: { id, studentId, studentName, expiresAt, isActive }
 * @param {string} [details.stage] - validation | existing_link_check | redemption
 * @param {string} [details.errorCode] - Stable Firebase/callable error code
 */
export async function logLinkingAttempt(details) {
  try {
    const {
      schoolId,
      parentId,
      parentEmail,
      parentName,
      codeEntered,
      status,
      errorDetail,
      codeData,
      stage,
      errorCode,
    } = details;

    // Determine which school collection to write to
    // If we have a schoolId (from found code or user's school), use it
    // Otherwise fall back - but we need at least SOME schoolId
    if (!schoolId) {
      console.warn('[parentLinkingService] No schoolId for linking attempt log, skipping');
      return;
    }

    const attemptsRef = collection(db, 'schools', schoolId, 'linkingAttempts');
    await addDoc(attemptsRef, {
      parentId: parentId || null,
      parentEmail: parentEmail || null,
      parentName: parentName || null,
      codeEntered: codeEntered || null,
      status,
      stage: stage || null,
      errorCode: errorCode || null,
      client: 'web',
      flowVersion: 2,
      errorDetail: errorDetail || null,
      codeData: codeData ? {
        id: codeData.id,
        studentId: codeData.studentId || null,
        studentName: codeData.studentName || null,
        expiresAt: codeData.expiresAt || null,
        isActive: codeData.isActive ?? null,
      } : null,
      attemptedAt: serverTimestamp(),
    });
  } catch (err) {
    // Never let logging failures break the linking flow
    console.error('[parentLinkingService] Failed to log linking attempt:', err);
  }
}

const parentLinkingService = {
  checkExistingLink,
  checkExistingLinkByCode,
  validateAccessCode,
  validateAccessCodeWithDiagnostics,
  completeParentSignup,
  redeemAccessCode,
  logLinkingAttempt,
};

export default parentLinkingService;
